import keyBy from 'lodash/keyBy';
import { handleIncomingPeople, handleIncomingLog, checkNewVisitors } from './helpers/log-tabs-model';
import { Logger } from '../common/logger';
import { isObject, map, uniq } from 'lodash';
import {
    defaultDataById,
    defaultTabs,
    FILTER_ID_ALL,
    FILTER_VISITOR_TURBINE,
    FILTER_VISITOR_VESSEL,
    TAB_ERROR,
    TAB_NOTES,
    TAB_RELEASE,
    TAB_VISITOR,
    TAB_WORK_ORDER,
} from './logTabs.utils';
import { checkWebsocketConnection } from 'common/saga-utils';

const initialState = {
    activeTabId: TAB_ERROR,
    tabsList: [TAB_ERROR, TAB_RELEASE, TAB_WORK_ORDER, TAB_VISITOR],
    turbineTabsList: [TAB_ERROR, TAB_RELEASE, TAB_WORK_ORDER, TAB_VISITOR, TAB_NOTES],
    tabsById: keyBy(defaultTabs, (raw) => raw.id),
    visitorsOnTurbine: [],
    visitorsOnVessel: [],
    dataById: defaultDataById,
};

export const logTabs = {
    name: 'logTabs',

    state: initialState,

    reducers: {
        setSelectedTab(state, payload) {
            Logger.info(`Selected tab: ${payload.id}`);
            state.activeTabId = payload.id; // Modify the draft directly
        },
        toggleTabVisibility(state, payload) {
            Logger.info(`Toggle tab visibility: ${payload.tabId}`);
            state.tabsById[payload.tabId].hidden = payload.hidden; // Modify the draft directly
        },

        setSelectedFilter(state, payload) {
            Logger.info(`Selected filter: ${payload.filterId}`);
            state.tabsById[payload.tabId].activeFilterId = payload.filterId; // Modify draft directly
        },

        toggleIsFilterDisabled(state, payload) {
            Logger.debug(`Tab ${payload.tabId} filter ${payload.filterId} is disabled: ${payload.disabled}`);
            const tabState = state.tabsById[payload.tabId];
            const filterIndex = tabState.filters.findIndex((f) => f.id === payload.filterId);

            if (filterIndex === -1) {
                Logger.error(`No filter ${payload.filterId} found for tab ${payload.tabId}`);
                return;
            }

            tabState.filters[filterIndex].disabled = payload.disabled; // Modify draft directly
        },

        updateAll(state, payload) {
            state.dataById = payload; // Modify draft directly
        },

        resetVisitors(state) {
            state.visitorsOnTurbine = []; // Modify draft directly
            state.visitorsOnVessel = []; // Modify draft directly
        },

        resetFilters(state) {
            state.tabsById = keyBy(defaultTabs, (raw) => raw.id); // Modify draft directly
        },

        loadMultipleAlarms2(state, payload) {
            Logger.debug(`LogTabs.loadMultipleAlarms Bulk updating ${payload.length}`, payload);
            state.dataById[TAB_ERROR] = {
                ...state.dataById[TAB_ERROR],
                ...keyBy(payload, (o) => o.entityKey),
            }; // Modify draft directly
        },
        loadMultipleVisitors2(state, payload) {
            Logger.debug(`LogTabs.loadMultipleVisitors Bulk updating ${payload.length}`, payload);

            const parsedPayload = keyBy(
                payload.map((item) => handleIncomingPeople(item)),
                (o) => o.entityKey
            );

            state.dataById.visitor = { ...state.dataById.visitor, ...parsedPayload }; // Modify draft directly
            state.visitorsOnTurbine = checkNewVisitors(state.visitorsOnTurbine, 'TURBINE', parsedPayload);
            state.visitorsOnVessel = checkNewVisitors(state.visitorsOnVessel, 'VESSEL', parsedPayload);
        },

        loadMultipleLogs2(state, payload) {
            Logger.debug(`LogTabs.loadMultipleLogs Bulk updating ${payload.length}`, payload);

            const parsedPayload = keyBy(
                payload.map((item) => handleIncomingLog(item)),
                (o) => o.entityKey
            );

            state.dataById.release = { ...state.dataById.release, ...parsedPayload }; // Modify draft directly
        },

        loadMultipleWorkOrders2(state, payload) {
            Logger.debug(`LogTabs.loadMultipleWorkOrders Bulk updating ${payload.length}`, payload);

            const updatedItems = payload.filter(
                (item) => item.is_deleted === undefined || item.is_deleted.value === false
            );
            const parsedPayload = keyBy(updatedItems, (o) => o.entityKey);

            state.dataById.workOrder = { ...state.dataById.workOrder, ...parsedPayload }; // Modify draft directly
        },
    },

    effects: (dispatch) => ({
        async fetchVisitorHistory() {
            await checkWebsocketConnection();
            dispatch({
                type: 'webSocket/requestPersonHistory',
                payload: {
                    entityType: 'PERSON',
                    entityKey: '*',
                    count: 100,
                    fields: [],
                },
            });
        },
        async selectTab(payload) {
            dispatch.logTabs.setSelectedTab(payload);
        },
        async reset() {
            Logger.info('Reset logTabs');
            dispatch.logTabs.resetVisitors();
            dispatch.logTabs.resetFilters();
            dispatch.logTabs.updateAll(defaultDataById);
            dispatch.logTabs.fetchVisitorHistory();
        },
        async switchTabMode(payload, state) {
            const { detailTabMode } = payload;
            Logger.info(`Switching tab mode, activate ${detailTabMode ? 'DETAIL' : FILTER_ID_ALL}`);
            const { activeTabId } = state.logTabs;
            if (detailTabMode) {
                dispatch.logTabs.toggleIsFilterDisabled({
                    tabId: TAB_VISITOR,
                    filterId: FILTER_VISITOR_VESSEL,
                    disabled: true,
                });
                if (activeTabId === TAB_VISITOR) {
                    const visitorTab = state.logTabs.tabsById[TAB_VISITOR];
                    if (visitorTab.activeFilterId === FILTER_VISITOR_VESSEL) {
                        dispatch.logTabs.setSelectedFilter({
                            tabId: TAB_VISITOR,
                            filterId: FILTER_VISITOR_TURBINE,
                        });
                    }
                }
                dispatch.logTabs.showTab({ tabId: TAB_NOTES });
            } else {
                dispatch.logTabs.toggleIsFilterDisabled({
                    tabId: TAB_VISITOR,
                    filterId: FILTER_VISITOR_VESSEL,
                    disabled: false,
                });
                if (activeTabId === TAB_NOTES) {
                    dispatch.logTabs.selectTab({ id: TAB_ERROR });
                }
                dispatch.logTabs.hideTab({ tabId: TAB_NOTES });
            }
        },
        async selectFilter(payload) {
            dispatch.logTabs.setSelectedFilter(payload);
            window.analytics.track('filter logs', { tab: payload.tabId, filter: payload.filterId });
        },
        async hideTab({ tabId }) {
            dispatch.logTabs.toggleTabVisibility({ tabId, hidden: true });
        },
        async showTab({ tabId }) {
            dispatch.logTabs.toggleTabVisibility({ tabId, hidden: false });
        },
        async loadMultipleAlarms(payload) {
            dispatch.logTabs.toggleFilterByTab({ tabId: TAB_ERROR, data: payload });
            dispatch.logTabs.loadMultipleAlarms2(payload);
        },
        async loadMultipleLogs(payload) {
            dispatch.logTabs.toggleFilterByTab({ tabId: TAB_RELEASE, data: payload });
            dispatch.logTabs.loadMultipleLogs2(payload);
        },
        async loadMultipleWorkOrders(payload) {
            dispatch.logTabs.toggleFilterByTab({ tabId: TAB_WORK_ORDER, data: payload });
            dispatch.logTabs.loadMultipleWorkOrders2(payload);
        },
        async loadMultipleVisitors(payload) {
            dispatch.logTabs.toggleFilterByTab({ tabId: TAB_VISITOR, data: payload });
            dispatch.logTabs.loadMultipleVisitors2(payload);
        },
        async toggleFilterByTab({ tabId, data }, state) {
            const allFilters = state.logTabs.tabsById[tabId].filters;
            const filters = allFilters.filter((f) => f.id !== FILTER_ID_ALL && f.disabled);
            if (filters.length === 0) {
                Logger.debug('No filters to enable. All are enabled.');
                return;
            }
            const filter = allFilters.filter((f) => f.id !== FILTER_ID_ALL)[0];
            const filterValuesByMapping = uniq(
                map(data, (d) => (isObject(d[filter.mapping]) ? d[filter.mapping].value : d[filter.mapping]))
            );
            const filtersToEnable = filters.filter((f) => filterValuesByMapping.includes(f.value));
            if (filtersToEnable.length === 0) {
                Logger.debug('No filters to enable from data.');
                return;
            }
            for (const f of filtersToEnable) {
                dispatch.logTabs.toggleIsFilterDisabled({ tabId, filterId: f.id, disabled: false });
            }
        },
    }),
};
